import moment from 'moment';
import { DateTime } from 'luxon';

import { getIdMap, isNullOrUndefined } from '../helpers/helpers';
import { getActiveMember } from '../helpers/users';
import { decoratePayloadWithLabels } from '../helpers/labels';
import { formatCustomFieldCreatePayload } from '../helpers/costcodeHelpers';
import {
  getProjectBucketTemplates,
  createProjectToBucketsMapByTemplate,
  createProjectToBucketsMap,
} from '../buckets/bucketHelpers';
import { collator } from '../common/helpers';

import { defaultFilters } from './projectFilters';

export default {};

export const CATEGORIES = ['Material', 'Overhead', 'Labor', 'Equipment'];

export const categoriesWithHours = new Set(['Overhead', 'Labor', 'Equipment']);
export const categoriesWithDays = new Set(['Equipment']);

export const condensePhasesAndCostcodes = (phases = [], costcodes = []) => {
  const phaseMap = {};
  const costcodeIdMap = getIdMap(costcodes);
  phases.forEach((phase) => {
    const {
      name,
      description,
      startDate,
      endDate,
      costcodeId,
      id,
      estimatedLabourHours,
      estimatedLabourCost,
      estimatedOverheadHours,
      estimatedOverheadCost,
      estimatedMaterialCost,
      estimatedEquipmentHours,
      estimatedEquipmentCost,
      categoryIds,
      categoryAssignments,
    } = phase;
    if (!(id in phaseMap)) {
      phaseMap[id] = {
        id,
        name,
        description,
        estimatedLabourHours,
        estimatedLabourCost,
        estimatedOverheadHours,
        estimatedOverheadCost,
        estimatedMaterialCost,
        estimatedEquipmentHours,
        estimatedEquipmentCost,
        costcodes: [],
      };
    }
    const ourPhase = phaseMap[id];
    if (costcodeId in costcodeIdMap) {
      ourPhase.costcodes.push({
        ...costcodeIdMap[costcodeId],
        startDate,
        endDate,
        categoryIds,
        categoryAssignments,
      });
    }
  });
  return Object.values(phaseMap);
};

const createPhaseLinkMap = (phases = []) => {
  const links = {};
  const idMap = {};
  phases.forEach((phase) => {
    const {
      costcodes = [],
      id,
    } = phase;
    costcodes.forEach((cc) => {
      links[`${id}-${cc.id}`] = {
        costcodeId: cc.id,
        phaseId: id,
        startDate: cc.startDate,
        endDate: cc.endDate,
        categoryAssignments: cc.categoryAssignments,
        categoryIds: cc.categoryIds,
      };
    });
    idMap[id] = phase;
  });
  return { links, idMap };
};

const momentsAreEqual = (m1, m2, granularity = 'day') => {
  const m1IsMoment = moment.isMoment(m1);
  const m2IsMoment = moment.isMoment(m2);
  if (m1IsMoment && m2IsMoment) return m1.isSame(m2, granularity);
  if (m1 === undefined || m1 === null) return m2 === undefined || m2 === null;
  return false;
};

export const momentToTimestamp = (date) => (date ? date.valueOf() : null);

export const getExpirationStatus = ({
  contractExpirationTimestamp,
  contractReminderTimestamp,
  contractExpiration: existingStatus,
}) => {
  if (existingStatus) return existingStatus;
  const now = DateTime.local().startOf('day');
  let contractExpiration = 'valid';
  if (contractExpirationTimestamp) {
    if (contractExpirationTimestamp < now) {
      contractExpiration = 'expired';
    } else if (contractReminderTimestamp && contractReminderTimestamp < now) {
      contractExpiration = 'soon';
    }
  }
  return contractExpiration;
};

/**
 * Filters projects based on the provided filter configuration.
 * @param {Object} params - The filter parameters
 * @param {Array} params.projects - The projects to filter
 * @param {Object} params.filters - The filter configuration from Redux
 * @param {Object} params.projectToLabel - Map of project IDs to their label IDs
 * @param {string} params.typeId - The current project type ID
 * @param {Array} params.filteredProjectIds - Project IDs from backend custom field filtering
 * @param {Array} params.buckets - Array of all buckets
 * @param {Array} params.bucketTemplates - Array of bucket templates
 * @returns {Array} The filtered projects
 */
export const filterProjects = ({
  projects = [],
  filters = {},
  projectToLabel = {},
  typeId,
  filteredProjectIds = null,
  buckets = [],
  bucketTemplates = [],
}) => {
  if (!projects?.length) return [];

  const {
    active = defaultFilters.active,
    projectTypeId = [],
    labels: filterLabels = [],
    contractExpiration = defaultFilters.contractExpiration,
    ...otherFilters
  } = filters || {};

  // Create set for custom field filtering if backend returned filtered IDs
  const filteredIdSet = filteredProjectIds ? new Set(filteredProjectIds) : null;

  // Extract bucket template filters (keys that match bucket template IDs)
  const projectBucketTemplates = getProjectBucketTemplates(bucketTemplates);
  const bucketTemplateIdSet = new Set(projectBucketTemplates.map((t) => t.id));
  const bucketTemplateFilters = {};
  Object.entries(otherFilters).forEach(([key, value]) => {
    if (bucketTemplateIdSet.has(key) && Array.isArray(value) && value.length > 0) {
      bucketTemplateFilters[key] = value;
    }
  });

  // Create a map of project ID -> array of bucket IDs for each bucket template
  const templateIds = Object.keys(bucketTemplateFilters);
  const projectToBucketsMap = createProjectToBucketsMapByTemplate(buckets, templateIds);

  return projects.filter((project) => {
    // Filter by custom field IDs from backend
    if (filteredIdSet && !filteredIdSet.has(project.id)) {
      return false;
    }

    // Filter by active status
    if (active?.length > 0 && !active.includes(project.active)) {
      return false;
    }

    // Filter by project type
    if (typeId) {
      if (project.projectTypeId !== typeId) return false;
    } else if (projectTypeId?.length > 0) {
      const projectType = project.projectTypeId ?? -1;
      if (!projectTypeId.includes(projectType)) {
        return false;
      }
    }

    // Filter by labels
    if (filterLabels?.length > 0) {
      const ourLabels = projectToLabel?.[project.id] ?? [];
      if (!ourLabels.some((label) => filterLabels.includes(label))) {
        return false;
      }
    }

    // Filter by contract expiration
    if (contractExpiration?.length > 0) {
      const expirationStatus = getExpirationStatus(project);
      if (!contractExpiration.includes(expirationStatus)) {
        return false;
      }
    }

    // Filter by bucket templates
    if (Object.keys(bucketTemplateFilters).length > 0) {
      const projectBuckets = projectToBucketsMap[project.id] || {};
      const projectBucketSets = Object.fromEntries(
        Object.entries(projectBuckets).map(([templateId, bucketIds]) => [
          templateId,
          new Set(bucketIds || []),
        ]),
      );

      const matchesAllTemplates = Object.entries(bucketTemplateFilters).every(
        ([templateId, selectedBucketIds]) => {
          const projectBucketIds = projectBucketSets[templateId] || new Set();
          return selectedBucketIds.some((bucketId) => projectBucketIds.has(bucketId));
        },
      );

      if (!matchesAllTemplates) {
        return false;
      }
    }

    return true;
  });
};

export const mergeProjectsAndCostcodes = ({
  timeEntryUserMap,
  projects,
  costcodes,
  users,
  teams,
  phases = [],
  projectEquipment = {},
  equipment = [],
  categoryAssignments = {},
}) => {
  const projectMap = getIdMap(projects.map((project) => ({
    ...project,
    projectCostcodes: [],
    activeUsers: [],
    inactiveUsers: [],
    users: [],
    phases: [],
  })));

  const equipmentMap = getIdMap(equipment);
  const teamsMap = getIdMap(teams);
  const projectUserAdded = {};
  const costcodeIdMap = {};

  costcodes.forEach((costcode) => {
    const { projectId, id } = costcode;
    costcodeIdMap[costcode.id] = costcode;
    if (!projectId || !(projectId in projectMap)) return;
    const ourProject = projectMap[projectId];
    if (!('projectCostcodes' in ourProject)) ourProject.projectCostcodes = [];
    ourProject.projectCostcodes.push({
      ...costcode,
      categoryAssignments: categoryAssignments?.[id]?.unphased ?? {},
    });
  });

  phases.forEach((phase) => {
    const {
      projectId,
      name,
      description,
      startDate,
      endDate,
      costcodeId,
      id,
      estimatedLabourHours,
      estimatedLabourCost,
      estimatedOverheadHours,
      estimatedMaterialCost,
      estimatedOverheadCost,
      estimatedEquipmentHours,
      estimatedEquipmentCost,
      categoryIds,
      categoryAssignments: phaseCategoryAssignments,
      customCategoryEstimates,
    } = phase;
    if (!projectId || !(projectId in projectMap)) return;
    const ourProject = projectMap[projectId];

    if (!('phases' in ourProject)) ourProject.phases = {};
    const ourPhases = ourProject.phases;
    if (!(id in ourPhases)) {
      ourPhases[id] = {
        id,
        name,
        description,
        estimatedLabourHours,
        estimatedLabourCost,
        estimatedOverheadHours,
        estimatedMaterialCost,
        estimatedOverheadCost,
        estimatedEquipmentCost,
        estimatedEquipmentHours,
        costcodes: [],
        customCategoryEstimates: customCategoryEstimates || [],
      };
    }
    const ourPhase = ourPhases[id];
    const resolvedCostcode = costcodeIdMap[costcodeId];
    if (!resolvedCostcode || resolvedCostcode.projectId !== projectId) return;
    ourPhase.costcodes.push({
      ...resolvedCostcode,
      categoryIds: categoryIds ?? resolvedCostcode.categoryIds,
      categoryAssignments: categoryAssignments?.[costcodeId]?.[id] ?? phaseCategoryAssignments,
      startDate,
      endDate,
    });
  });

  users.forEach((user) => {
    const { id: userId, projects: userProjects = [], teams: userTeams = [] } = user;

    let ourProjects = userProjects;
    userTeams?.forEach((team) => {
      if (team.id in teamsMap) {
        ourProjects = ourProjects.concat(teamsMap[team.id].projects);
      }
    });

    ourProjects.forEach(({ id }) => {
      if (id in projectMap) {
        if (!(id in projectUserAdded)) projectUserAdded[id] = new Set();
        if (projectUserAdded[id].has(user.id)) return;
        projectUserAdded[id].add(user.id);
        projectMap[id].users.push(user);
        if (getActiveMember({ user, projectId: id, timeEntryUserMap })) {
          projectMap[id].activeUsers.push(userId);
        } else {
          projectMap[id].inactiveUsers.push(userId);
        }
      }
    });
  });

  const projectValues = Object.values(projectMap);
  return projectValues.map((project) => {
    const {
      id: projectId,
      contractReminderTimestamp,
      contractExpirationTimestamp,
    } = project;
    const {
      [projectId]: ourProjectEquipment = {},
    } = projectEquipment;
    const fullProjEquip = [];
    const equipmentSchedules = [];

    Object.keys(ourProjectEquipment).forEach((eqId) => {
      const schedule = ourProjectEquipment[eqId];
      if (equipmentMap[eqId]) fullProjEquip.push(equipmentMap[eqId]);
      schedule.forEach(({ startTime, endTime }) => {
        equipmentSchedules.push({
          ...equipmentMap[eqId],
          id: `${eqId}.${startTime}`,
          startTime: startTime ? moment(startTime) : null,
          endTime: endTime ? moment(endTime) : null,
        });
      });
    });
    const phasesArray = Object.values(project?.phases || {});
    // Add phaseEstimate.{categoryId} properties to each phase
    const phasesWithEstimates = phasesArray.map((phase) => {
      const phaseWithEstimates = { ...phase };
      if (phase.customCategoryEstimates?.length > 0) {
        phase.customCategoryEstimates.forEach(({ categoryId, value }) => {
          if (value !== null && value !== undefined) {
            phaseWithEstimates[`phaseEstimate.${categoryId}`] = value;
          }
        });
      }
      return phaseWithEstimates;
    });

    return {
      ...project,
      contractExpiration: getExpirationStatus({
        contractExpirationTimestamp,
        contractReminderTimestamp,
      }),
      phases: phasesWithEstimates,
      equipment: fullProjEquip,
      equipmentSchedules,
    };
  });
};

const KEYS_TO_IGNORE = new Set(['id']);

const hasObjectChanged = (oldObj, newObj) => {
  const allKeys = Array.from(new Set([
    ...Object.keys(oldObj ?? {}),
    ...Object.keys(newObj ?? {}),
  ])).filter((key) => !KEYS_TO_IGNORE.has(key));
  return allKeys.some((key) => (
    oldObj?.[key] !== newObj?.[key]
  ));
};

export const compareCodes = (oldCode, newCode) => {
  const {
    categoryIds: oldCategoryIds = [],
    categoryAssignments: oldCategoryAssignments = {},
    ...restOldCode
  } = oldCode ?? {};
  const {
    categoryIds: newCategoryIds = [],
    categoryAssignments: newCategoryAssignments = {},
    ...restNewCode
  } = newCode ?? {};

  let changed = hasObjectChanged(restOldCode, restNewCode);

  const hasChangedCategoryValues = Object.keys(newCategoryAssignments).some((key) => {
    const newCategoryAssignment = newCategoryAssignments?.[key] ?? {};
    const oldCategoryAssignment = oldCategoryAssignments?.[key] ?? {};
    return hasObjectChanged(oldCategoryAssignment, newCategoryAssignment);
  }) || newCategoryIds.length !== oldCategoryIds.length;

  // Also check if newCode has fields that oldCode doesn't have
  const newFields = Object.keys(restNewCode).filter((key) => !(key in restOldCode));
  if (newFields.length > 0) {
    changed = true;
  }

  return changed || hasChangedCategoryValues ? newCode : null;
};

const phaseCodeHasChanged = (costcode, oldPhaseLinkRecord) => {
  const {
    categoryAssignments: ccCategoryAssignments = {},
  } = costcode ?? {};
  const {
    categoryAssignments: oldCCCategoryAssignments = {},
  } = oldPhaseLinkRecord ?? {};

  const hasChangedCategoryValues = Object.keys(ccCategoryAssignments).some((key) => {
    const ccCategoryAssignment = ccCategoryAssignments?.[key] ?? {};
    const oldCCCategoryAssignment = oldCCCategoryAssignments?.[key] ?? {};
    return hasObjectChanged(oldCCCategoryAssignment, ccCategoryAssignment);
  });

  return hasChangedCategoryValues
  || !momentsAreEqual(costcode.startDate, oldPhaseLinkRecord.startDate)
  || !momentsAreEqual(costcode.endDate, oldPhaseLinkRecord.endDate);
};

const phaseEstimateKeys = new Set([
  'estimatedLabourHours',
  'estimatedLabourCost',
  'estimatedOverheadHours',
  'estimatedOverheadCost',
  'estimatedMaterialCost',
  'estimatedEquipmentHours',
  'estimatedEquipmentCost',
]);

const isEstimateDifferent = (phase, oldPhase, key) => (phase?.[key] !== oldPhase?.[key]);

const phaseHasNewEstimates = (phase, oldPhase) => (
  Array.from(phaseEstimateKeys).some((key) => isEstimateDifferent(phase, oldPhase, key))
);

export const getCustomEstimateMaps = (phase, estimateKey) => {
  const phaseCustomEstimates = {};

  Object.keys(phase || {}).forEach((key) => {
    if (key.startsWith(estimateKey)) {
      const categoryId = key.replace(estimateKey, '');
      phaseCustomEstimates[categoryId] = phase[key];
    }
  });

  return phaseCustomEstimates;
};

const phaseHasNewCustomEstimates = (phase, oldPhase) => {
  const phaseCustomEstimates = getCustomEstimateMaps(phase, 'phaseEstimate.');
  const oldPhaseCustomEstimates = getCustomEstimateMaps(oldPhase, 'phaseEstimate.');

  // Check if any custom estimates are different
  const allCategoryIds = new Set([
    ...Object.keys(phaseCustomEstimates),
    ...Object.keys(oldPhaseCustomEstimates),
  ]);

  return Array.from(allCategoryIds).some((categoryId) => (
    phaseCustomEstimates[categoryId] !== oldPhaseCustomEstimates[categoryId]
  ));
};

export const generateCostcodePayload = ({
  newCostcodes,
  oldCostcodes = [],
  oldPhases = [],
  newPhases = oldPhases,
}) => {
  const oldCostcodeMap = getIdMap(oldCostcodes);
  const newCostcodeMap = newCostcodes ? getIdMap(newCostcodes) : {};
  const fromGlobalCostcodes = [];
  const createdCostcodes = [];
  const removedCostcodes = newCostcodes ? oldCostcodes
    .filter((cc) => !(cc.id in newCostcodeMap))
    .map((cc) => cc.id) : [];
  const modifiedCostcodes = [];

  if (newCostcodes) {
    newCostcodes.forEach((costcode) => {
      if (!(costcode.id in oldCostcodeMap)) {
        if (costcode.isNew) {
          createdCostcodes.push({
            id: costcode.id,
            code: costcode.code,
            name: costcode.name,
            description: costcode.description,
            phased: Boolean(costcode.phased),
            categoryIds: costcode.categoryIds,
            categoryAssignments: costcode.categoryAssignments,
            hourlyWage: costcode.hourlyWage,
            dailyWage: costcode.dailyWage,
            wageAdjustment: costcode.wageAdjustment,
            wageMultiplier: costcode.wageMultiplier,
            hourlyBillingRate: costcode.hourlyBillingRate,
            dailyBillingRate: costcode.dailyBillingRate,
            customData: costcode.customData,
            intuitId: costcode.intuitId,
          });
        } else {
          fromGlobalCostcodes.push({
            id: costcode.id,
            phased: Boolean(costcode.phased),
            categoryIds: costcode.categoryIds,
            categoryAssignments: costcode.categoryAssignments,
            hourlyWage: costcode.hourlyWage,
            dailyWage: costcode.dailyWage,
            wageAdjustment: costcode.wageAdjustment,
            wageMultiplier: costcode.wageMultiplier,
            hourlyBillingRate: costcode.hourlyBillingRate,
            dailyBillingRate: costcode.dailyBillingRate,
          });
        }
      } else {
        const oldCode = oldCostcodeMap[costcode.id];
        const codeChanged = compareCodes(oldCode, costcode);

        if (codeChanged) {
          modifiedCostcodes.push({
            phased: Boolean(codeChanged.phased),
            code: codeChanged.code,
            name: codeChanged.name,
            description: codeChanged.description,
            id: codeChanged.id,
            hourlyWage: codeChanged.hourlyWage,
            dailyWage: codeChanged.dailyWage,
            wageAdjustment: codeChanged.wageAdjustment,
            wageMultiplier: codeChanged.wageMultiplier,
            hourlyBillingRate: codeChanged.hourlyBillingRate,
            dailyBillingRate: codeChanged.dailyBillingRate,
            categoryAssignments: codeChanged.categoryAssignments,
            categoryIds: codeChanged.categoryIds,
          });
        }
      }
    });
  }

  const {
    links: oldPhaseLinks,
    idMap: oldPhaseMap,
  } = createPhaseLinkMap(oldPhases);

  const {
    links: newPhaseLinks,
    idMap: newPhaseMap,
  } = createPhaseLinkMap(newPhases);

  const createdPhases = [];
  const modifiedDetailPhases = [];
  const removedPhases = [];

  const createdPhaseLinks = [];
  const removePhaseLinks = [];
  const modifiedDetailPhaseLinks = [];

  if (newPhases) {
    newPhases.forEach((phase) => {
      // Extract phaseEstimate keys for custom categories
      const phaseCustomCategoryEstimatesMap = getCustomEstimateMaps(phase, 'phaseEstimate.');
      const phaseCustomCategoryEstimates = Object
        .entries(phaseCustomCategoryEstimatesMap)
        .map(([categoryId, value]) => ({ categoryId, value }))
        .filter(({ value }) => !isNullOrUndefined(value));

      if (phase.id in oldPhaseMap) {
        const oldPhase = oldPhaseMap[phase.id];

        if (
          oldPhase.name !== phase.name
          || oldPhase.description !== phase.description
          || phaseHasNewEstimates(phase, oldPhase)
          || phaseHasNewCustomEstimates(phase, oldPhase)
        ) {
          const updatedPhase = { phaseId: phase.id, name: phase.name };

          Array.from(phaseEstimateKeys).forEach((key) => {
            if (isEstimateDifferent(phase, oldPhase, key)) {
              updatedPhase[key] = phase[key];
            }
          });

          if (phaseCustomCategoryEstimates.length > 0) {
            updatedPhase.customCategoryEstimates = phaseCustomCategoryEstimates;
          }

          modifiedDetailPhases.push(updatedPhase);
        }

        phase.costcodes.forEach((costcode) => {
          const phaseCostcodeKey = `${phase.id}-${costcode.id}`;
          if (!(phaseCostcodeKey in oldPhaseLinks)) {
            createdPhaseLinks.push({
              phaseId: phase.id,
              costcodeId: costcode.id,
              startDate: momentToTimestamp(costcode.startDate),
              endDate: momentToTimestamp(costcode.endDate),
              categoryAssignments: costcode.categoryAssignments,
              categoryIds: costcode.categoryIds,
            });
          } else {
            const oldPhaseLinkRecord = oldPhaseLinks[phaseCostcodeKey];

            if (phaseCodeHasChanged(costcode, oldPhaseLinkRecord)) {
              modifiedDetailPhaseLinks.push({
                phaseId: phase.id,
                costcodeId: costcode.id,
                startDate: momentToTimestamp(costcode.startDate),
                endDate: momentToTimestamp(costcode.endDate),
                categoryAssignments: costcode.categoryAssignments,
                categoryIds: costcode.categoryIds,
              });
            }
          }
        });
      } else {
        const newPhase = {
          name: phase.name,
          description: phase.description,
          costcodes: phase.costcodes.map((cc) => ({
            id: cc.id,
            startDate: momentToTimestamp(cc.startDate),
            endDate: momentToTimestamp(cc.endDate),
            categoryIds: cc.categoryIds,
            categoryAssignments: cc.categoryAssignments,
          })),
        };

        Array.from(phaseEstimateKeys).forEach((key) => {
          if (!isNullOrUndefined(phase[key])) {
            newPhase[key] = phase[key];
          }
        });

        if (phaseCustomCategoryEstimates.length > 0) {
          newPhase.customCategoryEstimates = phaseCustomCategoryEstimates;
        }

        createdPhases.push(newPhase);
      }
    });
  }

  oldPhases.forEach((phase) => {
    if (phase.id in newPhaseMap) {
      phase.costcodes.forEach((costcode) => {
        const phaseCostcodeKey = `${phase.id}-${costcode.id}`;
        if (!(phaseCostcodeKey in newPhaseLinks)) {
          removePhaseLinks.push({
            phaseId: phase.id,
            costcodeId: costcode.id,
          });
        }
      });
    } else {
      removedPhases.push(phase.id);
    }
  });

  return {
    fromGlobalCostcodes,
    createdCostcodes,
    removedCostcodes,
    modifiedCostcodes,
    createdPhases,
    modifiedDetailPhases,
    removedPhases,
    createdPhaseLinks,
    removePhaseLinks,
    modifiedDetailPhaseLinks,
  };
};

export const generateEquipmentPayload = ({
  newEquipment = [],
  oldEquipment = [],
  equipmentSchedule: scheduleObject = {},
}) => {
  const oldSet = new Set(oldEquipment.map((eq) => eq.id));
  const newSet = new Set();

  const createdEquipment = [];
  const fromExistingEquipment = [];
  const removedEquipment = [];
  const equipmentSchedule = [];

  newEquipment.forEach((eq) => {
    if (eq.isNew) {
      createdEquipment.push(eq);
    } else {
      if (!oldSet.has(eq.id)) {
        fromExistingEquipment.push(eq.id);
      }
      newSet.add(eq.id);
    }
  });
  oldEquipment.forEach((eq) => {
    if (!newSet.has(eq.id)) {
      removedEquipment.push(eq.id);
    }
  });

  Object.keys(scheduleObject).forEach((key) => {
    const [equipmentId] = key.split('.');
    const {
      range: [startMoment, endMoment],
      name,
    } = scheduleObject[key];
    const payload = {
      startTime: momentToTimestamp(startMoment),
      endTime: momentToTimestamp(endMoment),
    };
    if (equipmentId.length < 36) {
      payload.name = name;
    } else {
      payload.equipmentId = equipmentId;
    }
    if (payload.startTime && payload.endTime) {
      equipmentSchedule.push(payload);
    }
  });

  return {
    createdEquipment,
    fromExistingEquipment,
    removedEquipment,
    equipmentSchedule,
  };
};

export const calculateExpirationReminder = ({
  contractExpirationTimestamp,
  contractReminderTimestamp,
  isNotDisplay,
}) => {
  if (!contractExpirationTimestamp || !contractReminderTimestamp) {
    return isNotDisplay ? { type: 'months', value: 3 } : null;
  }
  const expireDT = DateTime.fromMillis(contractExpirationTimestamp);
  const reminderDT = DateTime.fromMillis(contractReminderTimestamp);
  const months = expireDT.diff(reminderDT, 'months').as('months');
  if (months > 0 && Number.isInteger(months)) {
    return isNotDisplay ? { type: 'months', value: months } : `${months} Months`;
  }
  const weeks = expireDT.diff(reminderDT, 'weeks').as('weeks');
  if (weeks > 0 && Number.isInteger(weeks)) {
    return isNotDisplay ? { type: 'weeks', value: weeks } : `${weeks} Weeks`;
  }
  const days = expireDT.diff(reminderDT, 'days').as('days');
  return isNotDisplay ? { type: 'days', value: days } : `${days} Days`;
};

export const formatContractExpiration = (formValues = {}) => {
  const ourValues = { ...formValues };
  if (!ourValues.hasContract) {
    delete ourValues.hasContract;
    ourValues.contractExpirationTimestamp = null;
    ourValues.contractReminderTimestamp = null;
    return ourValues;
  }
  if (ourValues.contractExpiration && moment.isMoment(ourValues.contractExpiration)) {
    const expireMoment = ourValues.contractExpiration;
    ourValues.contractExpirationTimestamp = expireMoment.startOf('day').valueOf();
    const {
      contractReminder: {
        type: reminderType = 'months',
        value: reminderDuration = 3,
      } = {},
    } = ourValues;
    if (reminderType && reminderDuration !== undefined) {
      const newDuration = reminderDuration || 0;
      ourValues.contractReminderTimestamp = expireMoment
        .clone().startOf('day').subtract(reminderType, newDuration).valueOf();
    }
  }
  delete ourValues.contractExpiration;
  delete ourValues.contractReminder;
  delete ourValues.hasContract;
  return ourValues;
};

export const prepareProjectPayload = ({
  formValues = {},
  storedValues = {},
  divisions = {},
  showFiles,
  customFields = [],
}) => {
  if (!formValues) return {};

  let ourValues = formatContractExpiration(formValues);
  ourValues = decoratePayloadWithLabels(ourValues);
  if (ourValues.materialDiscount) {
    ourValues.materialDiscount /= 100;
  }
  if (ourValues.customer) {
    delete ourValues.customerId;
    delete ourValues.customer.id;
    ourValues.customer = decoratePayloadWithLabels(ourValues.customer);
  } else {
    delete ourValues.customer;
  }

  if (ourValues.billingContact) {
    ourValues.billingContact = ourValues.billingContact.trim();
  }

  const {
    costcodes = [],
    phases = [],
    geofence,
    fileMap = {},
    equipment = [],
    equipmentSchedule = {},
    projectTypeId,
    useCoordinates,
  } = storedValues;

  const ourFileMap = showFiles ? fileMap : {};
  const {
    fromGlobalCostcodes,
    createdCostcodes,
    createdPhases,
  } = generateCostcodePayload({
    newCostcodes: costcodes,
    newPhases: phases,
  });

  const {
    createdEquipment,
    fromExistingEquipment,
    equipmentSchedule: payloadSchedule,
  } = generateEquipmentPayload({
    newEquipment: equipment,
    equipmentSchedule,
  });

  // Extract all categoryEstimate values and remove from ourValues
  const customCategoryEstimatesMap = getCustomEstimateMaps(ourValues, 'categoryEstimate.');
  const customCategoryEstimates = [];
  Object.entries(customCategoryEstimatesMap).forEach(([categoryId, value]) => {
    if (!isNullOrUndefined(value)) {
      customCategoryEstimates.push({ categoryId, value });
    }
    delete ourValues[`categoryEstimate.${categoryId}`];
  });

  const payload = {
    ...ourValues,
    costcodes: fromGlobalCostcodes,
    phases: createdPhases,
    newCostcodes: createdCostcodes,
    geofence,
    createdEquipment,
    fromExistingEquipment,
    equipmentSchedule: payloadSchedule,
    projectTypeId,
    useCoordinates,
    customCategoryEstimates,
  };

  const { divisionId } = payload;
  const {
    [divisionId]: {
      name: divisionName,
    } = {},
  } = divisions;

  if (formValues.dateRange && formValues.dateRange.length === 2) {
    delete payload.dateRange;
    const [startDate, endDate] = formValues.dateRange;
    if (moment.isMoment(startDate)) payload.startDate = startDate.format('YYYY-MM-DD');
    if (moment.isMoment(endDate)) payload.endDate = endDate.format('YYYY-MM-DD');
  }

  const formattedPayload = formatCustomFieldCreatePayload({
    payload,
    customFields,
  });

  return {
    payload: formattedPayload,
    ourFileMap,
    divisionName,
  };
};

/**
   * Check if dates are being used in any costcodes
   * @param {array} data
   * @returns {boolean}
   */
export const hasDates = (ourData) => ourData.some((data) => data.costcodes
&& data.costcodes.some((cc) => cc.startDate && cc.endDate));

/**
   * Creates a map of categoryId to costcodes
   * @param {array} costcodes
   * @returns {object} category to code map
   */
export const costCodeCategoryMap = (costcodes = []) => {
  const ccMap = {};
  costcodes.forEach((costcode) => {
    const { categoryIds, categoryId } = costcode ?? {};
    // existing job costing templates may not have categoryIds
    const safeCategoryIds = categoryIds ?? [categoryId];
    safeCategoryIds?.forEach((cId) => {
      if (!cId) return;
      if (!(cId in ccMap)) {
        ccMap[cId] = [];
      }
      ccMap[cId].push({
        ...costcode,
        key: costcode.id,
      });
    });
  });
  return ccMap;
};

/**
 * Sums category estimate values
 * @param {array} costcodes
 * @param {obj} categories object of cc categories
 * @param {string} category
 * @param {string} field parameter to sum
 * @param {bool} shouldInferCost infer cost from wage * hours (used in build estimate)
 * @returns {number} sum
 */
export const totalCategoryValues = (
  costcodes,
  categories,
  category,
  field,
  shouldInferCost = false,
) => {
  const categoryMap = costCodeCategoryMap(costcodes);
  const {
    [category]: {
      id: categoryId,
    } = {},
  } = categories;
  const {
    [categoryId]: categoryCostCodes = [],
  } = categoryMap;

  if (!categoryCostCodes.length) return 0;

  const totalValues = categoryCostCodes.reduce((acc, cc) => {
    const {
      hourlyWage = 0,
      dailyWage = 0,
      categoryAssignments: {
        [categoryId]: costcodeCategory,
      } = {},
    } = cc;
    const {
      hours = 0,
      days = 0,
    } = costcodeCategory ?? {};

    let val = costcodeCategory?.[field] ?? 0;

    // For equipment costcodes, always calculate from days and hours when shouldInferCost is true
    if (shouldInferCost && category === 'Equipment') {
      if (field === 'estimatedCost') {
        // Prioritize manually entered estimatedCost over calculated values
        if (costcodeCategory?.estimatedCost !== undefined) {
          val = costcodeCategory.estimatedCost;
        } else {
          val = (days * dailyWage) + (hours * hourlyWage);
        }
      } else if (field === 'hours') {
        val = hours;
      }
    } else if (isNullOrUndefined(val) && shouldInferCost) {
      val = hours * hourlyWage;
    }

    return acc + val;
  }, 0);

  return (Math.round(totalValues * 100) / 100);
};

/**
 * Calculates budget tracking estimates
 * based on costcodes hours/costs
 * @param {object} categories costcode categories
 * @param {array} unphasedCostcodes
 * @param {array} phases
 * @param {object} customCategoriesMap
 */
export const calculateBuildEstimateValues = ({
  categories,
  unphasedCostcodes,
  phases,
  customCategoriesMap,
}) => {
  const {
    Material: { id: materialId } = {},
    Overhead: { id: overheadId } = {},
    Equipment: { id: equipmentId } = {},
  } = categories ?? {};

  let buildLabourHours = 0;
  let buildLabourCost = 0;
  let buildMaterialCost = 0;
  let buildOverheadCost = 0;
  let buildEquipmentCost = 0;
  const buildCustomCategoryCosts = {}; // Map of categoryId -> total cost

  // unphased
  unphasedCostcodes.forEach((cc) => {
    const {
      hourlyWage = 0,
      dailyWage = 0,
    } = cc;
    const { categoryAssignments } = cc;
    Object.values(categoryAssignments || {}).forEach((assignment) => {
      const {
        defaultCategoryId,
        customCategoryId,
        hours = 0,
        days = 0,
        estimatedCost = 0,
      } = assignment;
      const isCustomCategory = !!customCategoryId;

      let costToUse = estimatedCost;
      if (isNullOrUndefined(costToUse)) {
        if (defaultCategoryId === equipmentId) {
          costToUse = (days * dailyWage) + (hours * hourlyWage);
        } else {
          costToUse = hours * hourlyWage;
        }
      }

      if (isCustomCategory) {
        // Handle custom category
        if (!buildCustomCategoryCosts[customCategoryId]) {
          buildCustomCategoryCosts[customCategoryId] = 0;
        }
        buildCustomCategoryCosts[customCategoryId] += costToUse;
      } else if (defaultCategoryId === materialId) {
        buildMaterialCost += estimatedCost ?? 0;
      } else if (defaultCategoryId === overheadId) {
        buildOverheadCost += costToUse;
      } else if (defaultCategoryId === equipmentId) {
        buildEquipmentCost += costToUse;
      } else {
        buildLabourCost += costToUse;
        buildLabourHours += hours;
      }
    });
  });

  // takes phase estimate, otherwise sums costcode values
  phases.forEach((phase) => {
    buildLabourHours += phase?.estimatedLabourHours ?? totalCategoryValues(phase.costcodes, categories, 'Labor', 'hours', false);
    buildLabourCost += phase?.estimatedLabourCost ?? totalCategoryValues(phase.costcodes, categories, 'Labor', 'estimatedCost', true);
    buildMaterialCost += phase?.estimatedMaterialCost ?? totalCategoryValues(phase.costcodes, categories, 'Material', 'estimatedCost', true);
    buildOverheadCost += phase?.estimatedOverheadCost ?? totalCategoryValues(phase.costcodes, categories, 'Overhead', 'estimatedCost', true);
    buildEquipmentCost += phase?.estimatedEquipmentCost ?? totalCategoryValues(phase.costcodes, categories, 'Equipment', 'estimatedCost', true);

    // Handle custom categories in phases
    Object.values(customCategoriesMap || {}).forEach((customCategory) => {
      const { id: customCategoryId, name: customCategoryName } = customCategory;
      const phaseEstimate = phase?.[`phaseEstimate.${customCategoryId}`];
      const costToUse = phaseEstimate ?? totalCategoryValues(
        phase.costcodes,
        categories,
        customCategoryName,
        'estimatedCost',
        true,
      );

      if (!buildCustomCategoryCosts[customCategoryId]) {
        buildCustomCategoryCosts[customCategoryId] = 0;
      }
      buildCustomCategoryCosts[customCategoryId] += costToUse;
    });
  });

  return {
    buildLabourHours,
    buildLabourCost,
    buildMaterialCost,
    buildOverheadCost,
    buildEquipmentCost,
    buildCustomCategoryCosts,
  };
};

export const formatProjectLabelFromCompanySettings = ({ name, number, settings = {} }) => {
  const { projectLabelFormat = 'name' } = settings;

  switch (projectLabelFormat) {
    case 'number': {
      if (isNullOrUndefined(number) || number === '') break;
      return number;
    }
    case 'both': {
      if (isNullOrUndefined(number) || number === '') break;
      return `${number} - ${name}`;
    }
    default: {
      return name;
    }
  }
  return name;
};

export const formatProjectLabelsFromDropdownValues = ({
  values = [],
  projectIdMap = {},
  settings = {},
}) => {
  const formattedValues = values.map(({ id }) => {
    const {
      [id]: {
        name,
        number,
      } = {},
    } = projectIdMap;

    return formatProjectLabelFromCompanySettings({ name, number, settings });
  });

  return formattedValues;
};

export const compareProjectLabel = (a, b, projectIdMap, settings) => {
  const { name: nameA, number: numberA } = projectIdMap[a.projectId] ?? {};
  const { name: nameB, number: numberB } = projectIdMap[b.projectId] ?? {};
  const projectLabelA = formatProjectLabelFromCompanySettings({
    name: nameA,
    number: numberA,
    settings,
  });
  const projectLabelB = formatProjectLabelFromCompanySettings({
    name: nameB,
    number: numberB,
    settings,
  });

  return collator.compare(projectLabelA ?? '', projectLabelB ?? '');
};

export const getBucketTemplateProjectMap = ({
  bucketTemplates = [],
  buckets = [],
}) => {
  /*
    {
      [bucketTemplateId]: {
        name,
        projectMap: {
            [projectId]: [bucketId1, bucketId2, ...]
        }
      }
    }
  */
  const bucketTemplateIdToProjectToBucketsMap = {};

  const bucketsByTemplate = buckets.reduce((acc, bucket) => {
    const { bucketTemplateId } = bucket;
    if (!acc[bucketTemplateId]) {
      acc[bucketTemplateId] = [];
    }
    acc[bucketTemplateId].push(bucket);
    return acc;
  }, {});
  const projectBucketTemplates = getProjectBucketTemplates(bucketTemplates);
  projectBucketTemplates.forEach((bucketTemplate) => {
    const templateBuckets = bucketsByTemplate[bucketTemplate.id] ?? [];
    if (!templateBuckets.length) return;
    const projectToBucketsMap = createProjectToBucketsMap(templateBuckets, bucketTemplate.id);
    bucketTemplateIdToProjectToBucketsMap[bucketTemplate.id] = {
      name: bucketTemplate.name,
      projectMap: projectToBucketsMap,
    };
  });
  return bucketTemplateIdToProjectToBucketsMap;
};